import React, { useEffect, useState } from "react";
import API from "../../api/axiosConfig";
import { motion } from "framer-motion";
import { Star, CheckCircle, XCircle, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

export default function CompanyReviews() {
  const user = JSON.parse(localStorage.getItem("user"));
  const companyId = user?.companyId ?? user?.company?.id ?? user?.company?._id;
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [averages, setAverages] = useState({});
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchReviews();
    // eslint-disable-next-line
  }, []);

  const fetchReviews = async () => {
    if (!companyId) return;
    try {
      const res = await API.get(`/reviews/company/${companyId}`);
      setReviews(res.data?.data?.reviews ?? []);
      setAverages(res.data?.data?.averages ?? {});
    } catch (err) {
      console.error(err);
      toast.error("Error cargando reseñas");
    }
  };

  const approve = async (id) => {
    try {
      await API.post(`/reviews/approve/${id}`);
      toast.success("Reseña aprobada");
      fetchReviews();
    } catch (err) {
      toast.error("Error aprobando");
    }
  };

  const reject = async (id) => {
    try {
      await API.delete(`/reviews/reject/${id}`);
      toast.success("Reseña rechazada");
      fetchReviews();
    } catch (err) {
      toast.error("Error rechazando");
    }
  };

  const filteredReviews = reviews.filter((r) => {
    if (filter === "all") return true;
    if (filter === "pending") return !r.visible;
    if (filter === "approved") return r.visible;
    return true;
  });

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 mb-4 text-supernova-600 hover:text-supernova-700"
      >
        <ArrowLeft size={20} />
        Volver
      </button>
      <h2 className="text-3xl font-bold text-gray-800 mb-6">
        Reseñas de productos
      </h2>

      <div className="flex gap-4 mb-4 text-sm text-gray-600">
        <span>Total: {reviews.length}</span>
        <span>Pendientes: {reviews.filter((r) => !r.visible).length}</span>
        <span>Aprobadas: {reviews.filter((r) => r.visible).length}</span>
      </div>

      <div className="flex gap-3 mb-6">
        <button
          onClick={() => setFilter("all")}
          className={`px-4 py-2 rounded-xl border ${
            filter === "all"
              ? "bg-supernova-500 text-white"
              : "bg-white text-gray-700"
          }`}
        >
          Todas
        </button>

        <button
          onClick={() => setFilter("pending")}
          className={`px-4 py-2 rounded-xl border ${
            filter === "pending"
              ? "bg-yellow-500 text-white"
              : "bg-white text-gray-700"
          }`}
        >
          Pendientes
        </button>

        <button
          onClick={() => setFilter("approved")}
          className={`px-4 py-2 rounded-xl border ${
            filter === "approved"
              ? "bg-green-500 text-white"
              : "bg-white text-gray-700"
          }`}
        >
          Aprobadas
        </button>
      </div>

      <div className="mb-10 bg-white p-5 rounded-xl shadow-sm">
        <h3 className="text-xl font-semibold mb-3">Promedio por producto</h3>

        {Object.keys(averages).length === 0 ? (
          <p className="text-gray-500">No hay reseñas aún.</p>
        ) : (
          <div className="flex flex-col gap-4">
            {Object.keys(averages).map((productId) => {
              const avg = averages[productId].total / averages[productId].count;

              const product = reviews.find(
                (r) => (r.product?.id ?? r.product?._id) === productId
              )?.product;

              return (
                <div
                  key={productId}
                  className="border p-4 rounded-xl bg-gray-50"
                >
                  <p className="font-semibold text-gray-800">
                    {product?.title}
                  </p>

                  <div className="flex items-center gap-1 mt-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star
                        key={n}
                        size={20}
                        className={`${
                          avg >= n
                            ? "text-yellow-400 fill-yellow-400"
                            : "text-gray-300"
                        }`}
                      />
                    ))}
                    <span className="ml-2 text-sm text-gray-700">
                      {avg.toFixed(1)} / 5
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {filteredReviews.length === 0 ? (
        <p className="text-gray-500 text-center">No hay reseñas.</p>
      ) : (
        <div className="flex flex-col gap-4">
          {filteredReviews.map((r) => (
            <motion.div
              key={r.id ?? r._id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border p-5 rounded-xl shadow-sm"
            >
              <div className="flex justify-between">
                <h3 className="font-semibold text-gray-800">
                  {r.product?.title}
                </h3>

                <span
                  className={`px-3 py-1 text-xs rounded-full ${
                    r.visible
                      ? "bg-green-100 text-green-600"
                      : "bg-yellow-100 text-yellow-600"
                  }`}
                >
                  {r.visible ? "Visible" : "Pendiente"}
                </span>
              </div>

              <div className="flex items-center gap-1 mt-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={n}
                    size={18}
                    className={`${
                      r.rating >= n
                        ? "text-yellow-400 fill-yellow-400"
                        : "text-gray-300"
                    }`}
                  />
                ))}
              </div>

              <p className="text-gray-700 mt-3">{r.comment}</p>

              <p className="text-gray-400 text-sm mt-2">
                Por: {r.user?.name || "Usuario"}
              </p>

              <div className="flex gap-3 mt-4">
                {!r.visible && (
                  <button
                    onClick={() => approve(r.id ?? r._id)}
                    className="bg-supernova-500 text-white px-4 py-2 rounded-xl hover:bg-supernova-600 flex items-center gap-2"
                  >
                    <CheckCircle size={18} /> Aprobar
                  </button>
                )}

                <button
                  onClick={() => reject(r.id ?? r._id)}
                  className="bg-red-500 text-white px-4 py-2 rounded-xl hover:bg-red-600 flex items-center gap-2"
                >
                  <XCircle size={18} /> Rechazar
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
